// File path: src/utils/jobRiskEngine.js
// Purpose: Shared manufacturing risk engine for ManuCalc.
// Used by: Job Builder, ManuPlan, Operation Builder, Quote Engine, Reports.

import {
  getFinishRequirementById,
  getMachineById,
  getMaterialById,
  getToleranceClassById,
} from "../data/manufacturingKnowledge";

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const unique = (items) => [...new Set(items.filter(Boolean))];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeMachineId = (value) => {
  if (!value) return "cnc-mill-3-axis";

  const direct = getMachineById(value);
  if (direct?.id === value) return direct.id;

  const normalizedValue = normalizeText(value);
  const knownIds = [
    "cnc-mill-3-axis",
    "cnc-mill-4-axis",
    "cnc-mill-5-axis",
    "manual-mill",
    "cnc-lathe",
    "manual-lathe",
    "mill-turn",
    "wire-edm",
    "sinker-edm",
    "surface-grinder",
    "cylindrical-grinder",
    "drill-press",
    "waterjet",
    "laser-cutter",
  ];

  const match = knownIds.find((id) => normalizeText(getMachineById(id).name) === normalizedValue || normalizeText(id) === normalizedValue);
  return match || "cnc-mill-3-axis";
};

const normalizeMachineRoute = (route) => {
  if (!Array.isArray(route) || route.length === 0) return ["cnc-mill-3-axis"];
  return unique(route.map(normalizeMachineId));
};

const normalizeMaterialId = (value) => {
  if (!value) return "aluminum-6061";

  const direct = getMaterialById(value);
  if (direct?.id === value) return direct.id;

  const normalizedValue = normalizeText(value);
  const knownIds = [
    "aluminum-6061",
    "aluminum-7075",
    "mild-steel",
    "cold-rolled-steel",
    "hot-rolled-steel",
    "stainless-304",
    "stainless-316",
    "titanium-6al4v",
    "brass-360",
    "delrin-acetal",
  ];

  const match = knownIds.find((id) => normalizeText(getMaterialById(id).name) === normalizedValue || normalizeText(id) === normalizedValue);
  return match || "aluminum-6061";
};

export const normalizeJobForRiskEngine = (job = {}) => ({
  ...job,
  materialId: normalizeMaterialId(job.materialId || job.material),
  machineRoute: normalizeMachineRoute(job.machineRoute),
});

export const getRiskLabel = (riskScore) => {
  if (riskScore >= 18) return "Severe";
  if (riskScore >= 12) return "High";
  if (riskScore >= 6) return "Medium";
  return "Low";
};

export const getManufacturabilityGrade = (riskScore) => {
  if (riskScore >= 20) return "D";
  if (riskScore >= 14) return "C";
  if (riskScore >= 8) return "B";
  return "A";
};

export const getConfidenceLabel = (riskScore) => {
  if (riskScore >= 14) return "Low";
  if (riskScore >= 7) return "Medium";
  return "High";
};

export const getMachineRouteSummary = (job) => {
  const normalizedJob = normalizeJobForRiskEngine(job);
  const route = normalizeMachineRoute(normalizedJob.machineRoute);
  const machines = route.map(getMachineById);

  return {
    machines,
    machineNames: machines.map((machine) => machine.name),
    families: unique(machines.map((machine) => machine.family)),
    hasMill: machines.some((machine) => machine.family === "Milling"),
    hasLathe: machines.some((machine) => machine.family === "Turning"),
    hasGrinding: machines.some((machine) => machine.family === "Grinding"),
    hasEdm: machines.some((machine) => machine.family === "EDM"),
    hasCutting: machines.some((machine) => machine.family === "Cutting"),
    hasHybrid: machines.some((machine) => machine.family === "Hybrid"),
    setupTransferCount: Math.max(0, machines.length - 1),
  };
};

export const estimateSetupCount = (job) => {
  const normalizedJob = normalizeJobForRiskEngine(job);
  const routeSummary = getMachineRouteSummary(normalizedJob);
  const features = normalizedJob.features || {};
  let setupCount = 1;

  if (features.sideFeatures) setupCount += 1;
  if (features.undersideFeatures) setupCount += 1;
  if (features.complex3D && !routeSummary.hasHybrid) setupCount += 1;
  if (routeSummary.setupTransferCount > 0) setupCount += routeSummary.setupTransferCount;

  return clamp(setupCount, 1, 8);
};

export const analyzeJobRisk = (job) => {
  const normalizedJob = normalizeJobForRiskEngine(job);
  const material = getMaterialById(normalizedJob.materialId);
  const toleranceClass = getToleranceClassById(normalizedJob.toleranceClassId);
  const finishRequirement = getFinishRequirementById(normalizedJob.finishRequirementId);
  const routeSummary = getMachineRouteSummary(normalizedJob);
  const features = normalizedJob.features || {};

  const dimensions = normalizedJob.dimensions || {};
  const stock = normalizedJob.stock || {};

  const partLength = numberOrZero(dimensions.length);
  const partWidth = numberOrZero(dimensions.width);
  const partHeight = numberOrZero(dimensions.height);
  const stockLength = numberOrZero(stock.length);
  const stockWidth = numberOrZero(stock.width);
  const stockHeight = numberOrZero(stock.height);
  const quantity = Math.max(1, numberOrZero(normalizedJob.quantity));

  const partVolume = partLength * partWidth * partHeight;
  const stockVolume = stockLength * stockWidth * stockHeight;
  const materialRemoval = Math.max(stockVolume - partVolume, 0);
  const materialRemovalPercent = stockVolume > 0 ? (materialRemoval / stockVolume) * 100 : 0;

  const stockAllowance = {
    length: stockLength - partLength,
    width: stockWidth - partWidth,
    height: stockHeight - partHeight,
  };

  const largestDimension = Math.max(partLength, partWidth, partHeight, 0.001);
  const smallestDimension = Math.max(Math.min(partLength || 999, partWidth || 999, partHeight || 999), 0.001);
  const aspectRatio = largestDimension / smallestDimension;
  const setupCount = estimateSetupCount(normalizedJob);

  const warnings = [];
  const recommendations = [];
  const costDrivers = [];
  const inspectionPoints = [];
  const operationNotes = [];
  const failureModes = [];
  const decisionBreakdown = [];

  let riskScore = 0;

  riskScore += material.riskModifier || 0;
  if (material.riskModifier) {
    decisionBreakdown.push(`+${material.riskModifier} risk from ${material.name} machinability.`);
  }

  riskScore += toleranceClass.riskModifier || 0;
  if (toleranceClass.riskModifier) {
    decisionBreakdown.push(`+${toleranceClass.riskModifier} risk from ${toleranceClass.name} tolerance class.`);
  }

  riskScore += finishRequirement.riskModifier || 0;
  if (finishRequirement.riskModifier) {
    decisionBreakdown.push(`+${finishRequirement.riskModifier} risk from ${finishRequirement.name}.`);
  }

  if (partLength <= 0 || partWidth <= 0 || partHeight <= 0 || stockLength <= 0 || stockWidth <= 0 || stockHeight <= 0) {
    riskScore += 12;
    warnings.push("Invalid part or stock dimensions detected.");
    recommendations.push("Enter valid positive dimensions before setup planning or quoting.");
    failureModes.push("Setup plan cannot be trusted until dimensions are valid.");
    decisionBreakdown.push("+12 risk because one or more dimensions are invalid.");
  }

  if (stockAllowance.length < 0 || stockAllowance.width < 0 || stockAllowance.height < 0) {
    riskScore += 14;
    warnings.push("Finished part dimensions exceed stock size.");
    recommendations.push("Increase stock size before machining. Current stock cannot clean up to the finished part.");
    failureModes.push("Part will fail to clean up from current stock.");
    decisionBreakdown.push("+14 risk because stock is undersized.");
  }

  if (stockAllowance.length >= 0 && stockAllowance.length < 0.05) {
    riskScore += 2;
    warnings.push("Length cleanup allowance is very tight.");
    recommendations.push("Verify saw cut accuracy and burr condition before loading stock.");
  }

  if (stockAllowance.width >= 0 && stockAllowance.width < 0.05) {
    riskScore += 2;
    warnings.push("Width cleanup allowance is very tight.");
    recommendations.push("Confirm stock width before relying on cleanup passes.");
  }

  if (stockAllowance.height >= 0 && stockAllowance.height < 0.03) {
    riskScore += 2;
    warnings.push("Thickness cleanup allowance is very tight.");
    recommendations.push("Face both sides carefully and verify final thickness before finish operations.");
  }

  if (materialRemovalPercent > 70) {
    riskScore += 3;
    warnings.push("High material removal detected.");
    recommendations.push("Rough the part first, let it relax if needed, then finish critical datums.");
    costDrivers.push("High stock removal increases cycle time, tool wear, and heat.");
    failureModes.push("Part movement after aggressive roughing due to stress relief.");
    decisionBreakdown.push("+3 risk because more than 70% of stock may be removed.");
  } else if (materialRemovalPercent > 45) {
    riskScore += 1;
    costDrivers.push("Moderate stock removal will affect roughing time and tool wear.");
  }

  if (features.thinWalls) {
    riskScore += 4;
    warnings.push("Thin-wall feature risk detected.");
    recommendations.push("Leave support stock during roughing and finish thin walls late with light radial engagement.");
    failureModes.push("Wall deflection, spring-back, chatter, or clamp distortion.");
    decisionBreakdown.push("+4 risk from thin-wall features.");
  }

  if (features.pockets) {
    riskScore += 1;
    operationNotes.push("Pocketing requires chip evacuation, tool reach planning, and corner-radius strategy.");
    costDrivers.push("Pocketing increases toolpath time and cutter engagement.");
  }

  if (features.slots) {
    riskScore += 1;
    operationNotes.push("Slots should avoid full-width engagement when possible. Rough undersize, then finish walls.");
    costDrivers.push("Slots increase chip packing and tool-load risk.");
  }

  if (features.tappedHoles) {
    riskScore += 2;
    warnings.push("Tapped/threaded holes increase late-stage scrap risk.");
    recommendations.push("Chamfer before tapping and consider thread milling on expensive or critical parts.");
    failureModes.push("Broken tap or poor thread quality after significant machine time has already been spent.");
    decisionBreakdown.push("+2 risk from tapped/threaded holes.");
  }

  if (features.sideFeatures) {
    riskScore += 2;
    warnings.push("Side features require additional access or another orientation.");
    recommendations.push("Use soft jaws, an angle fixture, a 4th-axis operation, or a controlled second setup.");
    failureModes.push("Side feature location shift caused by second-op clamping variation.");
    decisionBreakdown.push("+2 risk from side features.");
  }

  if (features.undersideFeatures) {
    riskScore += 2;
    warnings.push("Underside features require a flip operation.");
    recommendations.push("Create reliable first-op datums before flipping the part.");
    failureModes.push("Backside feature misalignment after flipping.");
    decisionBreakdown.push("+2 risk from underside features.");
  }

  if (features.complex3D) {
    riskScore += 3;
    warnings.push("Complex 3D geometry increases programming, finish, and tool-reach risk.");
    recommendations.push("Use roughing, semi-finishing, rest machining, and controlled finishing passes.");
    costDrivers.push("3D surfacing can dominate programming and cycle time.");
    decisionBreakdown.push("+3 risk from complex 3D geometry.");
  }

  if (features.criticalFinish) {
    riskScore += 2;
    warnings.push("Critical finish surfaces require handling and deburring control.");
    recommendations.push("Protect finish-critical faces during second operations and deburring.");
    failureModes.push("Cosmetic rejection from jaw marks, burrs, scratches, or poor finish passes.");
    decisionBreakdown.push("+2 risk from critical finish features.");
  }

  if (setupCount > 1) {
    const setupRisk = Math.min(setupCount - 1, 5);
    riskScore += setupRisk;
    warnings.push(`${setupCount} estimated setups or machine transfers detected.`);
    recommendations.push("Define primary, secondary, and tertiary datums before machining begins.");
    inspectionPoints.push("Inspect datum relationships after each setup transition.");
    failureModes.push("Tolerance stack-up or feature shift between setups.");
    decisionBreakdown.push(`+${setupRisk} risk from estimated setup count.`);
  }

  if (routeSummary.setupTransferCount > 0) {
    riskScore += routeSummary.setupTransferCount;
    warnings.push("Multi-machine workflow requires controlled datum transfer.");
    recommendations.push("Document how the part will be located between machines before starting the job.");
    inspectionPoints.push("Verify transfer datums before each machine handoff.");
    decisionBreakdown.push(`+${routeSummary.setupTransferCount} risk from machine-to-machine transfer.`);
  }

  if (routeSummary.hasLathe && (features.pockets || features.slots || features.sideFeatures) && !routeSummary.hasMill && !routeSummary.hasHybrid) {
    riskScore += 5;
    warnings.push("Milled features selected, but route does not include a mill or mill-turn machine.");
    recommendations.push("Add CNC mill, manual mill, or mill-turn operation to the machine route.");
    failureModes.push("Selected machine route cannot realistically produce all selected features.");
    decisionBreakdown.push("+5 risk because selected features require milling capability.");
  }

  if (features.criticalFinish && !routeSummary.hasGrinding && finishRequirement.id === "ground") {
    riskScore += 3;
    warnings.push("Ground finish requested, but no grinding operation is in the route.");
    recommendations.push("Add surface grinder or cylindrical grinder depending on the controlling surface.");
  }

  if (aspectRatio > 6) {
    riskScore += 2;
    warnings.push("High aspect-ratio part may be unstable during machining.");
    recommendations.push("Use additional support, reduce tool pressure, and consider sequencing roughing symmetrically.");
    failureModes.push("Chatter, bowing, lift, or deflection from long unsupported geometry.");
  }

  if (quantity >= 10) {
    operationNotes.push("Quantity justifies repeatable stops, fixture references, or soft jaws.");
    costDrivers.push("Repeatability and loading time become important at this quantity.");
  }

  if (quantity >= 25) {
    recommendations.push("Compare fixture build time against repeated setup/loading time before quoting.");
    costDrivers.push("Dedicated fixturing may reduce cost over the full quantity.");
  }

  inspectionPoints.push("Verify stock size before cutting.");
  inspectionPoints.push("Inspect first datum surface before using it for downstream features.");

  if (toleranceClass.id !== "general") {
    inspectionPoints.push("Inspect critical dimensions after roughing and again after finishing.");
  }

  if (features.holes || features.tappedHoles) {
    inspectionPoints.push("Verify hole pattern from datums before committing to full quantity.");
  }

  if (warnings.length === 0) warnings.push("No major setup risks detected from the current job inputs.");
  if (recommendations.length === 0) recommendations.push("Current plan appears reasonable. Confirm tooling, workholding, and inspection strategy before cutting.");
  if (operationNotes.length === 0) operationNotes.push("Build operations in order from datum creation to roughing, finishing, and final inspection.");
  if (costDrivers.length === 0) costDrivers.push("Primary cost drivers appear to be setup time, machine time, and inspection effort.");
  if (failureModes.length === 0) failureModes.push("No obvious failure mode detected from the current inputs.");

  const finalRiskScore = clamp(Math.round(riskScore), 0, 30);

  return {
    riskScore: finalRiskScore,
    riskLabel: getRiskLabel(finalRiskScore),
    manufacturabilityGrade: getManufacturabilityGrade(finalRiskScore),
    confidenceLabel: getConfidenceLabel(finalRiskScore),
    setupCount,
    material,
    toleranceClass,
    finishRequirement,
    routeSummary,
    stockAllowance,
    materialRemovalPercent: Number(materialRemovalPercent.toFixed(1)),
    aspectRatio: Number(aspectRatio.toFixed(2)),
    warnings: unique(warnings),
    recommendations: unique(recommendations),
    costDrivers: unique(costDrivers),
    inspectionPoints: unique(inspectionPoints),
    operationNotes: unique(operationNotes),
    failureModes: unique(failureModes),
    decisionBreakdown: unique(decisionBreakdown),
  };
};

export const buildRecommendedMachineRoute = (job) => {
  const normalizedJob = normalizeJobForRiskEngine(job);
  const features = normalizedJob.features || {};
  const finishRequirement = getFinishRequirementById(normalizedJob.finishRequirementId);
  const route = [];

  if (features.complex3D) {
    route.push("cnc-mill-5-axis");
  } else if (features.pockets || features.slots || features.sideFeatures || features.undersideFeatures || features.holes) {
    route.push("cnc-mill-3-axis");
  }

  if (features.tappedHoles && !route.includes("cnc-mill-3-axis")) {
    route.push("cnc-mill-3-axis");
  }

  if (finishRequirement.id === "ground" || features.criticalFinish) {
    route.push("surface-grinder");
  }

  if (route.length === 0) route.push("cnc-mill-3-axis");

  return normalizeMachineRoute(unique(route));
};

export const buildJobSummary = (job) => {
  const normalizedJob = normalizeJobForRiskEngine(job);
  const analysis = analyzeJobRisk(normalizedJob);
  const recommendedRoute = buildRecommendedMachineRoute(normalizedJob);

  return {
    jobId: normalizedJob.id,
    jobName: normalizedJob.name,
    risk: analysis,
    recommendedRoute,
    recommendedMachineNames: recommendedRoute.map((machineId) => getMachineById(machineId).name),
    executiveSummary: `${normalizedJob.name || "This job"} is rated ${analysis.riskLabel} risk with a manufacturability grade of ${analysis.manufacturabilityGrade}. Estimated setup count is ${analysis.setupCount}.`,
  };
};
